import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCopy, FiPlus, FiStar, FiTrash2, FiUpload } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { readStoredUser } from "../../utils/sessionUser";
import { useOnboardingFlow } from "../OnboardingFlowContext";
import {
  BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS,
  BOOKING_ENGINE_EMAIL_PREVIEW_VARS,
  amenityLabel,
  buildBookingEngineDisclosurePreview,
  buildBookingEngineDraftPayload,
  buildBookingEnginePreviewParagraphs,
  buildBookingEngineProfilePayload,
  buildBookingEngineProfilePreview,
  buildBookingEnginePublicUrl,
  buildBookingEngineRoomAmenitiesPayload,
  buildBookingEngineRoomCopyPayload,
  buildBookingEngineRoomCopyPreviewData,
  countBookingEngineImages,
  createBookingEngineTerm,
  createEmptyBookingEngineDraft,
  createEmptyBookingEngineProfile,
  ensureBookingEngineGuestEmailTemplates,
  getBookingEngineStepMessages,
  getBookingEngineText,
  normalizeAmenitiesRows,
  normalizeBookingEngineDraft,
  normalizeBookingEngineEditorLanguage,
  normalizeBookingEngineProfile,
  normalizeBookingEngineRoomAmenities,
  normalizeBookingEngineRoomCopyMap,
  normalizeBookingEngineRoomCopyRow,
  normalizeBookingEngineRoomImages,
  normalizeOnboardingRoomCategories,
  renderBookingEngineTemplateValue,
  roomCopyLanguageIsConfigured,
  sortBookingEngineImages,
  buildDefaultBookingEngineRoomCopy,
} from "../bookingEngineStepShared";
import { getOnboardingMessages } from "../onboardingI18n";
import "../OnboardingApp.css";

const BOOKING_ENGINE_STEP_CSS = `
  .onboarding-bookingLayout {
    display: grid;
    gap: 18px;
  }

  .onboarding-bookingNotice {
    border-radius: 18px;
    padding: 16px 18px;
    border: 1px solid var(--onb-card-border);
    background: linear-gradient(135deg, rgba(92, 191, 255, 0.12), rgba(255, 200, 87, 0.12));
    color: var(--onb-shell-text);
  }

  .onboarding-bookingNotice strong {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .onboarding-bookingSplit {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
    gap: 16px;
  }

  .onboarding-bookingPreviewCard {
    border-radius: 18px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-soft-background);
    padding: 16px;
    display: grid;
    gap: 12px;
  }

  .onboarding-bookingPreviewCard h5,
  .onboarding-bookingRoomCard h5 {
    margin: 0;
    font-size: 1rem;
  }

  .onboarding-bookingPreviewCard p,
  .onboarding-bookingRoomCard p {
    margin: 0;
    color: var(--onb-body-text);
    line-height: 1.6;
  }

  .onboarding-bookingProfileLines {
    display: grid;
    gap: 6px;
    color: var(--onb-shell-text);
  }

  .onboarding-bookingProfileName {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--onb-heading-color);
  }

  .onboarding-bookingInlineAdd {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
  }

  .onboarding-bookingChipRow {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .onboarding-bookingChip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 999px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-soft-background);
    color: var(--onb-shell-text);
    font-size: 0.92rem;
  }

  .onboarding-bookingChip button {
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
  }

  .onboarding-bookingHeroPreview {
    position: relative;
    overflow: hidden;
    border-radius: 22px;
    min-height: 260px;
    border: 1px solid var(--onb-card-border);
    background:
      linear-gradient(180deg, rgba(6, 10, 18, 0.18), rgba(6, 10, 18, 0.72)),
      linear-gradient(135deg, rgba(92, 191, 255, 0.16), rgba(255, 200, 87, 0.18));
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: flex-end;
  }

  .onboarding-bookingHeroCopy {
    padding: 22px;
    display: grid;
    gap: 10px;
  }

  .onboarding-bookingHeroCopy span {
    font-size: 0.82rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
  }

  .onboarding-bookingHeroCopy h4 {
    margin: 0;
    color: #ffffff;
    font-size: 1.7rem;
  }

  .onboarding-bookingHeroCopy p {
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    max-width: 640px;
  }

  .onboarding-bookingPricingPreview {
    border-radius: 18px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-soft-background);
    padding: 16px;
    display: grid;
    gap: 6px;
  }

  .onboarding-bookingPricingLabel {
    font-size: 0.82rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--onb-meta-label-color);
  }

  .onboarding-bookingPricingValue {
    color: var(--onb-shell-text);
    font-weight: 700;
    font-size: 1rem;
  }

  .onboarding-bookingLanguageGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .onboarding-bookingLanguageCard {
    border-radius: 18px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-soft-background);
    padding: 16px;
    display: grid;
    gap: 14px;
  }

  .onboarding-bookingLanguageCardHead {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
  }

  .onboarding-bookingTokenWrap {
    display: grid;
    gap: 8px;
  }

  .onboarding-bookingTokenRow {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .onboarding-bookingTokenBtn {
    border: 1px solid var(--onb-card-border);
    background: var(--onb-soft-background);
    color: var(--onb-shell-text);
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 0.82rem;
    cursor: pointer;
  }

  .onboarding-bookingPreviewShell {
    border-radius: 16px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-step-background);
    padding: 14px;
    display: grid;
    gap: 10px;
  }

  .onboarding-bookingPreviewShell h6 {
    margin: 0;
    color: var(--onb-heading-color);
  }

  .onboarding-bookingPreviewParagraph {
    margin: 0;
    color: var(--onb-shell-text);
    white-space: pre-wrap;
    line-height: 1.6;
  }

  .onboarding-bookingTermsGrid {
    display: grid;
    gap: 14px;
  }

  .onboarding-bookingTermCard {
    border-radius: 18px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-soft-background);
    padding: 16px;
    display: grid;
    gap: 14px;
  }

  .onboarding-bookingTermHead {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
  }

  .onboarding-bookingRoomGrid {
    display: grid;
    gap: 16px;
  }

  .onboarding-bookingRoomCard {
    border-radius: 22px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-soft-background);
    overflow: hidden;
  }

  .onboarding-bookingRoomHero {
    min-height: 180px;
    padding: 18px;
    display: flex;
    align-items: flex-end;
    background:
      linear-gradient(180deg, rgba(8, 12, 22, 0.12), rgba(8, 12, 22, 0.78)),
      linear-gradient(135deg, rgba(92, 191, 255, 0.18), rgba(255, 200, 87, 0.14));
    background-size: cover;
    background-position: center;
  }

  .onboarding-bookingRoomHeroInner {
    display: grid;
    gap: 8px;
  }

  .onboarding-bookingRoomHeroInner h4 {
    margin: 0;
    color: #ffffff;
    font-size: 1.35rem;
  }

  .onboarding-bookingRoomHeroInner p {
    color: rgba(255, 255, 255, 0.84);
  }

  .onboarding-bookingRoomBody {
    padding: 16px;
    display: grid;
    gap: 16px;
  }

  .onboarding-bookingRoomTabs {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .onboarding-bookingTab {
    border: 1px solid var(--onb-card-border);
    border-radius: 999px;
    background: transparent;
    color: var(--onb-shell-text);
    padding: 8px 12px;
    font-weight: 600;
    cursor: pointer;
  }

  .onboarding-bookingTab.is-active {
    background: var(--onb-primary-background);
    color: var(--onb-primary-text);
    border-color: transparent;
  }

  .onboarding-bookingThumbGrid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }

  .onboarding-bookingThumbCard {
    border-radius: 18px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-step-background);
    padding: 12px;
    display: grid;
    gap: 10px;
  }

  .onboarding-bookingThumbMedia {
    border-radius: 14px;
    min-height: 120px;
    background-size: cover;
    background-position: center;
    background-color: rgba(10, 15, 28, 0.55);
  }

  .onboarding-bookingThumbActions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .onboarding-bookingAmenityToolbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 220px auto;
    gap: 10px;
    align-items: center;
  }

  .onboarding-bookingPillRow {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .onboarding-bookingPill {
    border-radius: 999px;
    padding: 8px 12px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-step-background);
    color: var(--onb-shell-text);
    cursor: pointer;
    font-size: 0.9rem;
  }

  .onboarding-bookingPill.is-active {
    background: var(--onb-primary-background);
    color: var(--onb-primary-text);
    border-color: transparent;
  }

  .onboarding-bookingAmenityList {
    display: grid;
    gap: 14px;
  }

  .onboarding-bookingAmenityCategory {
    display: grid;
    gap: 10px;
  }

  .onboarding-bookingAmenityCategory h6 {
    margin: 0;
    color: var(--onb-meta-label-color);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.78rem;
  }

  .onboarding-bookingRoomCopyPreview {
    border-radius: 18px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-step-background);
    overflow: hidden;
  }

  .onboarding-bookingRoomCopyMedia {
    min-height: 180px;
    background:
      linear-gradient(180deg, rgba(8, 12, 22, 0.12), rgba(8, 12, 22, 0.78)),
      linear-gradient(135deg, rgba(92, 191, 255, 0.18), rgba(255, 200, 87, 0.14));
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: flex-end;
    padding: 16px;
  }

  .onboarding-bookingRoomCopyMedia span {
    color: rgba(255, 255, 255, 0.88);
    font-weight: 700;
  }

  .onboarding-bookingRoomCopyBody {
    padding: 16px;
    display: grid;
    gap: 12px;
  }

  .onboarding-bookingRoomCopyBody h5 {
    margin: 0;
    color: var(--onb-heading-color);
  }

  .onboarding-bookingRoomCopyMeta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .onboarding-bookingRoomCopyMetaCard {
    border-radius: 14px;
    border: 1px solid var(--onb-card-border);
    background: var(--onb-soft-background);
    padding: 12px;
    display: grid;
    gap: 6px;
  }

  .onboarding-bookingRoomCopyMetaCard span {
    font-size: 0.78rem;
    color: var(--onb-meta-label-color);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .onboarding-bookingRoomCopyMetaCard strong {
    color: var(--onb-shell-text);
  }

  .onboarding-bookingSummaryBadge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 999px;
    background: var(--onb-highlight-tag-background);
    color: var(--onb-highlight-tag-text);
    padding: 8px 12px;
    font-weight: 700;
  }

  @media (max-width: 1180px) {
    .onboarding-bookingSplit,
    .onboarding-bookingLanguageGrid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 900px) {
    .onboarding-bookingAmenityToolbar {
      grid-template-columns: 1fr;
    }

    .onboarding-bookingRoomCopyMeta {
      grid-template-columns: 1fr;
    }
  }
`;

function insertTokenIntoValue(currentValue, token, input) {
  const safeValue = String(currentValue || "");
  const safeToken = String(token || "");

  if (!input || typeof input.selectionStart !== "number" || typeof input.selectionEnd !== "number") {
    return { value: `${safeValue}${safeToken}`, cursor: safeValue.length + safeToken.length };
  }

  const start = input.selectionStart;
  const end = input.selectionEnd;
  return {
    value: `${safeValue.slice(0, start)}${safeToken}${safeValue.slice(end)}`,
    cursor: start + safeToken.length,
  };
}

function normalizeHeroAssetSource(heroAssetSource) {
  if (!heroAssetSource || typeof heroAssetSource !== "object") return null;
  const assetId = Number(heroAssetSource.id || heroAssetSource.asset_id || 0) || null;
  const publicUrl = String(heroAssetSource.public_url || heroAssetSource.url || "").trim();
  if (!assetId && !publicUrl) return null;
  return {
    id: assetId,
    asset_id: assetId,
    kind: String(heroAssetSource.kind || "hero_background").trim().toLowerCase(),
    public_url: publicUrl,
  };
}

function BookingEngineRoomCopyPreview({ category, row, image, previewLanguage, copy }) {
  const preview = buildBookingEngineRoomCopyPreviewData(category, row, previewLanguage);
  const previewOption =
    BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.find((option) => option.id === preview.lang) ||
    BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS[0];
  const configured = roomCopyLanguageIsConfigured(category, row, previewLanguage);

  return (
    <div className="onboarding-bookingRoomCopyPreview">
      <div
        className="onboarding-bookingRoomCopyMedia"
        style={image?.public_url ? { backgroundImage: `linear-gradient(180deg, rgba(8, 12, 22, 0.12), rgba(8, 12, 22, 0.78)), url(${image.public_url})` } : undefined}
      >
        {!image?.public_url ? <span>{copy.roomNoImage}</span> : null}
      </div>

      <div className="onboarding-bookingRoomCopyBody">
        <div className="onboarding-bookingChipRow">
          <span className="onboarding-bookingChip">
            {copy.roomCopyPreviewLanguage}: {previewOption.shortLabel}
          </span>
          <span className="onboarding-bookingChip">
            {configured ? copy.statusConfigured : copy.statusMissing}
          </span>
        </div>

        <h5>{preview.displayName || copy.roomCopyPreviewEmpty}</h5>
        {preview.specsLine ? <div className="onboarding-bookingSummaryBadge">{preview.specsLine}</div> : null}
        <p>{preview.shortDescription || copy.roomCopyPreviewEmpty}</p>

        {preview.bedType || preview.view ? (
          <div className="onboarding-bookingRoomCopyMeta">
            {preview.bedType ? (
              <div className="onboarding-bookingRoomCopyMetaCard">
                <span>{copy.roomCopyBedTypeLabel}</span>
                <strong>{preview.bedType}</strong>
              </div>
            ) : null}

            {preview.view ? (
              <div className="onboarding-bookingRoomCopyMetaCard">
                <span>{copy.roomCopyViewLabel}</span>
                <strong>{preview.view}</strong>
              </div>
            ) : null}
          </div>
        ) : null}

        {preview.highlightItems.length ? (
          <div>
            <div className="onboarding-bookingPricingLabel">{copy.roomCopyHighlightsLabel}</div>
            <div className="onboarding-bookingChipRow" style={{ marginTop: "8px" }}>
              {preview.highlightItems.map((item) => (
                <span key={`${category?.id}-${preview.lang}-${item}`} className="onboarding-bookingChip">
                  {item}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function BookingEngineStep() {
  const sessionUser = readStoredUser();
  const businessUnitId = Number(sessionUser?.active_business_unit_id || 0) || null;
  const { languageId } = usePmsTheme();
  const { clearStepCommandState, markStepComplete, markStepIncomplete, setStepCommandState, steps } =
    useOnboardingFlow();
  const onboardingCopy = getOnboardingMessages(languageId);
  const copy = getBookingEngineStepMessages(languageId);
  const previewLanguage = normalizeBookingEngineEditorLanguage(languageId);
  const [draft, setDraft] = useState(() => createEmptyBookingEngineDraft());
  const [profile, setProfile] = useState(() => createEmptyBookingEngineProfile());
  const [roomCategories, setRoomCategories] = useState([]);
  const [amenitiesAll, setAmenitiesAll] = useState([]);
  const [roomCategoryImages, setRoomCategoryImages] = useState({});
  const [roomCategoryCopyById, setRoomCategoryCopyById] = useState({});
  const [roomAmenityIdsById, setRoomAmenityIdsById] = useState({});
  const [sessionMeta, setSessionMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [heroBgFile, setHeroBgFile] = useState(null);
  const [heroBgAsset, setHeroBgAsset] = useState(null);
  const [heroBgUploading, setHeroBgUploading] = useState(false);
  const [roomCategoryImageFileById, setRoomCategoryImageFileById] = useState({});
  const [roomImageBusyByCategoryId, setRoomImageBusyByCategoryId] = useState({});
  const [roomTabByCategoryId, setRoomTabByCategoryId] = useState({});
  const [roomAmenityQueryByCategoryId, setRoomAmenityQueryByCategoryId] = useState({});
  const [roomAmenityCategoryByCategoryId, setRoomAmenityCategoryByCategoryId] = useState({});
  const [emailInsertTargetByLang, setEmailInsertTargetByLang] = useState({ en: "subject", es: "subject" });
  const emailFieldRefs = useRef({});
  const stepNumber = useMemo(
    () => Math.max(steps.findIndex((step) => step.id === "booking-engine") + 1, 1),
    [steps]
  );

  const amenityCategories = useMemo(
    () =>
      Array.from(new Set((amenitiesAll || []).map((item) => item.category).filter(Boolean))).sort((first, second) =>
        String(first).localeCompare(String(second))
      ),
    [amenitiesAll]
  );

  const guestEmailTemplates = useMemo(
    () => ensureBookingEngineGuestEmailTemplates(draft),
    [draft]
  );

  const publicBookingUrl = useMemo(
    () => buildBookingEnginePublicUrl(draft.booking_engine_slug),
    [draft.booking_engine_slug]
  );

  const profilePreview = useMemo(() => buildBookingEngineProfilePreview(profile), [profile]);
  const heroPreviewSubtitle =
    getBookingEngineText(draft, "hero_subtitle", previewLanguage) ||
    (previewLanguage === "es" ? copy.heroSubtitlePlaceholderEs : copy.heroSubtitlePlaceholderEn);
  const heroPreviewClaim =
    getBookingEngineText(draft, "hero_claim", previewLanguage) ||
    (previewLanguage === "es" ? copy.heroClaimPlaceholderEs : copy.heroClaimPlaceholderEn);
  const pricingDisclosurePreview = useMemo(
    () => buildBookingEngineDisclosurePreview(draft, copy),
    [copy, draft]
  );

  const emailTokens = useMemo(() => {
    if (String(languageId || "").toLowerCase().startsWith("es") || String(languageId || "").toLowerCase().startsWith("ca")) {
      return [
        { token: "{{HotelName}}", label: "Nombre hotel" },
        { token: "{{GuestName}}", label: "Nombre huesped" },
        { token: "{{GuestEmail}}", label: "Email huesped" },
        { token: "{{BookingReference}}", label: "Referencia reserva" },
        { token: "{{CheckInDate}}", label: "Fecha entrada" },
        { token: "{{CheckOutDate}}", label: "Fecha salida" },
        { token: "{{RoomsCount}}", label: "Numero habitaciones" },
        { token: "{{TotalPrice}}", label: "Precio total" },
        { token: "{{NightlyPrices}}", label: "Precios por noche" },
        { token: "{{CancelUrl}}", label: "Enlace cancelacion" },
      ];
    }

    return [
      { token: "{{HotelName}}", label: "Hotel name" },
      { token: "{{GuestName}}", label: "Guest name" },
      { token: "{{GuestEmail}}", label: "Guest email" },
      { token: "{{BookingReference}}", label: "Booking reference" },
      { token: "{{CheckInDate}}", label: "Check-in date" },
      { token: "{{CheckOutDate}}", label: "Check-out date" },
      { token: "{{RoomsCount}}", label: "Rooms count" },
      { token: "{{TotalPrice}}", label: "Total price" },
      { token: "{{NightlyPrices}}", label: "Nightly prices" },
      { token: "{{CancelUrl}}", label: "Cancel link" },
    ];
  }, [languageId]);

  const formattedSavedAt = useMemo(() => {
    if (!sessionMeta?.updated_at) return copy.summaryNotSaved;
    const parsedDate = new Date(sessionMeta.updated_at);
    if (Number.isNaN(parsedDate.getTime())) return copy.summaryNotSaved;
    return new Intl.DateTimeFormat(languageId || "en", { dateStyle: "short", timeStyle: "short" }).format(parsedDate);
  }, [copy.summaryNotSaved, languageId, sessionMeta?.updated_at]);

  const totalRoomImages = useMemo(() => countBookingEngineImages(roomCategoryImages), [roomCategoryImages]);
  const totalSelectedAmenities = useMemo(
    () =>
      Object.values(roomAmenityIdsById || {}).reduce(
        (total, values) => total + (Array.isArray(values) ? values.length : 0),
        0
      ),
    [roomAmenityIdsById]
  );
  const configuredRoomCount = useMemo(
    () =>
      roomCategories.filter((category) => {
        const row = roomCategoryCopyById?.[Number(category.id)] || buildDefaultBookingEngineRoomCopy(category);
        return roomCopyLanguageIsConfigured(category, row, "en") || roomCopyLanguageIsConfigured(category, row, "es");
      }).length,
    [roomCategories, roomCategoryCopyById]
  );
  const configuredEmailLanguages = useMemo(
    () =>
      BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.filter((option) => {
        const template = guestEmailTemplates?.[option.id] || {};
        return Boolean(String(template.subject || "").trim() || String(template.body || "").trim());
      }).length,
    [guestEmailTemplates]
  );
  const mediaBusy = useMemo(
    () => heroBgUploading || Object.values(roomImageBusyByCategoryId || {}).some(Boolean),
    [heroBgUploading, roomImageBusyByCategoryId]
  );

  const updateDirtyState = useCallback(() => {
    setIsDirty(true);
    setFeedback({ type: "", message: "" });
  }, []);

  const updateDraft = useCallback(
    (updater) => {
      setDraft((currentDraft) =>
        typeof updater === "function" ? updater(currentDraft) : { ...currentDraft, ...updater }
      );
      updateDirtyState();
    },
    [updateDirtyState]
  );

  const updateProfile = useCallback(
    (updater) => {
      setProfile((currentProfile) =>
        typeof updater === "function" ? updater(currentProfile) : { ...currentProfile, ...updater }
      );
      updateDirtyState();
    },
    [updateDirtyState]
  );

  const updateRoomCopy = useCallback(
    (category, updater) => {
      const categoryId = Number(category?.id || 0);
      if (!categoryId) return;
      setRoomCategoryCopyById((currentMap) => {
        const currentRow = normalizeBookingEngineRoomCopyRow(currentMap?.[categoryId]) || buildDefaultBookingEngineRoomCopy(category);
        const nextRow =
          typeof updater === "function" ? updater(currentRow) : { ...currentRow, ...(updater || {}) };
        return { ...(currentMap || {}), [categoryId]: nextRow };
      });
      updateDirtyState();
    },
    [updateDirtyState]
  );

  const refreshMediaSnapshot = useCallback(async () => {
    try {
      const response = await axios.get("/api/onboarding/booking-engine");
      const data = response.data || {};
      const heroAssetSource = data?.hero_asset || data?.heroBgAsset || data?.assets?.hero_background || data?.assets?.hero || null;
      const roomImagesSource =
        data?.room_category_images || data?.images?.room_categories || data?.booking_engine_room_category_images || {};

      setHeroBgAsset(normalizeHeroAssetSource(heroAssetSource));
      setRoomCategoryImages(normalizeBookingEngineRoomImages(roomImagesSource));
      if (data?.session) {
        setSessionMeta(data.session);
        markStepComplete("booking-engine");
      }
    } catch (error) {
      console.error("[Onboarding][BookingEngine] Error refreshing media snapshot:", error);
    }
  }, [markStepComplete]);

  const loadStepData = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setFeedback({ type: "", message: "" });

    try {
      const [bookingResponse, roomCategoriesResponse, amenitiesResponse] = await Promise.all([
        axios.get("/api/onboarding/booking-engine"),
        axios.get("/api/onboarding/room-categories"),
        businessUnitId
          ? axios.get("/api/amenities", { params: { business_unit_id: businessUnitId } })
          : Promise.resolve({ data: { rows: [] } }),
      ]);

      const bookingData = bookingResponse.data || {};
      const stagedCategories = normalizeOnboardingRoomCategories(roomCategoriesResponse.data);
      const heroAssetSource =
        bookingData?.hero_asset || bookingData?.heroBgAsset || bookingData?.assets?.hero_background || bookingData?.assets?.hero || null;
      const bookingAmenities = normalizeAmenitiesRows(bookingData?.amenities);

      setDraft(normalizeBookingEngineDraft(bookingData?.draft || bookingData?.settings || bookingData));
      setProfile(normalizeBookingEngineProfile(bookingData?.profile || bookingData?.booking_engine_profile || bookingData?.public_profile));
      setRoomCategories(stagedCategories);
      setAmenitiesAll(bookingAmenities.length ? bookingAmenities : normalizeAmenitiesRows(amenitiesResponse.data));
      setHeroBgAsset(normalizeHeroAssetSource(heroAssetSource));
      setRoomCategoryImages(
        normalizeBookingEngineRoomImages(
          bookingData?.room_category_images ||
            bookingData?.images?.room_categories ||
            bookingData?.booking_engine_room_category_images ||
            {}
        )
      );
      setRoomCategoryCopyById(
        normalizeBookingEngineRoomCopyMap(
          bookingData?.room_category_copy ||
            bookingData?.room_category_copies ||
            bookingData?.booking_engine_room_categories ||
            {}
        )
      );
      setRoomAmenityIdsById(
        normalizeBookingEngineRoomAmenities(
          bookingData?.room_category_amenities ||
            bookingData?.room_category_amenity_ids ||
            bookingData?.amenities_by_category ||
            {}
        )
      );
      setSessionMeta(bookingData?.session || null);
      setIsDirty(false);
      setLoadFailed(false);

      if (bookingData?.session) {
        markStepComplete("booking-engine");
      } else {
        markStepIncomplete("booking-engine");
      }
    } catch (error) {
      console.error("[Onboarding][BookingEngine] Error loading draft:", error);
      setDraft(createEmptyBookingEngineDraft());
      setProfile(createEmptyBookingEngineProfile());
      setRoomCategories([]);
      setAmenitiesAll([]);
      setHeroBgAsset(null);
      setRoomCategoryImages({});
      setRoomCategoryCopyById({});
      setRoomAmenityIdsById({});
      setSessionMeta(null);
      setLoadFailed(true);
      markStepIncomplete("booking-engine");
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.toasts.loadError });
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, copy.toasts.loadError, markStepComplete, markStepIncomplete]);

  useEffect(() => {
    loadStepData();
  }, [loadStepData]);

  useEffect(() => {
    if (!copiedUrl) return undefined;
    const timeoutId = window.setTimeout(() => setCopiedUrl(false), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [copiedUrl]);

  const addProfilePhone = () => {
    const value = String(phoneInput || "").trim();
    if (!value) return;
    updateProfile((currentProfile) => ({
      ...currentProfile,
      phones: [...(Array.isArray(currentProfile.phones) ? currentProfile.phones : []), value],
    }));
    setPhoneInput("");
  };

  const addProfileEmail = () => {
    const value = String(emailInput || "").trim();
    if (!value) return;
    updateProfile((currentProfile) => ({
      ...currentProfile,
      emails: [...(Array.isArray(currentProfile.emails) ? currentProfile.emails : []), value],
    }));
    setEmailInput("");
  };

  const updateGuestEmailTemplate = useCallback(
    (languageKey, patch) => {
      updateDraft((currentDraft) => ({
        ...currentDraft,
        booking_engine_guest_email_templates: {
          ...ensureBookingEngineGuestEmailTemplates(currentDraft),
          [languageKey]: {
            ...(ensureBookingEngineGuestEmailTemplates(currentDraft)?.[languageKey] || { subject: "", body: "" }),
            ...(patch || {}),
          },
        },
      }));
    },
    [updateDraft]
  );

  const setEmailFieldRef = (languageKey, fieldKey, element) => {
    const refKey = `${languageKey}:${fieldKey}`;
    if (element) {
      emailFieldRefs.current[refKey] = element;
    } else {
      delete emailFieldRefs.current[refKey];
    }
  };

  const insertGuestEmailToken = (languageKey, token) => {
    const fieldKey = emailInsertTargetByLang?.[languageKey] === "body" ? "body" : "subject";
    const template = guestEmailTemplates?.[languageKey] || { subject: "", body: "" };
    const refKey = `${languageKey}:${fieldKey}`;
    const input = emailFieldRefs.current[refKey];
    const { value, cursor } = insertTokenIntoValue(template?.[fieldKey], token, input);

    updateGuestEmailTemplate(languageKey, { [fieldKey]: value });

    window.requestAnimationFrame(() => {
      const target = emailFieldRefs.current[refKey];
      if (target && typeof target.setSelectionRange === "function") {
        target.focus();
        target.setSelectionRange(cursor, cursor);
      }
    });
  };

  const addTerm = () => {
    updateDraft((currentDraft) => ({
      ...currentDraft,
      booking_engine_terms: [
        ...currentDraft.booking_engine_terms,
        createBookingEngineTerm({
          sort_order: (currentDraft.booking_engine_terms.length + 1) * 10,
        }),
      ],
    }));
  };

  const updateTerm = (termId, updater) => {
    updateDraft((currentDraft) => ({
      ...currentDraft,
      booking_engine_terms: currentDraft.booking_engine_terms.map((term) => {
        if (String(term.id) !== String(termId)) return term;
        return typeof updater === "function" ? updater(term) : { ...term, ...(updater || {}) };
      }),
    }));
  };

  const removeTerm = (termId) => {
    updateDraft((currentDraft) => ({
      ...currentDraft,
      booking_engine_terms: currentDraft.booking_engine_terms.filter(
        (term) => String(term.id) !== String(termId)
      ),
    }));
  };

  const toggleAmenity = (categoryId, amenityId) => {
    const resolvedCategoryId = Number(categoryId);
    const resolvedAmenityId = Number(amenityId);
    if (!resolvedCategoryId || !resolvedAmenityId) return;

    setRoomAmenityIdsById((currentMap) => {
      const currentIds = Array.isArray(currentMap?.[resolvedCategoryId]) ? currentMap[resolvedCategoryId] : [];
      const nextIds = currentIds.includes(resolvedAmenityId)
        ? currentIds.filter((value) => value !== resolvedAmenityId)
        : [...currentIds, resolvedAmenityId];
      return { ...(currentMap || {}), [resolvedCategoryId]: nextIds };
    });
    updateDirtyState();
  };

  const clearAmenities = (categoryId) => {
    const resolvedCategoryId = Number(categoryId);
    if (!resolvedCategoryId) return;
    setRoomAmenityIdsById((currentMap) => ({ ...(currentMap || {}), [resolvedCategoryId]: [] }));
    updateDirtyState();
  };

  const handleCopyBookingUrl = async () => {
    if (!publicBookingUrl) {
      setFeedback({ type: "error", message: copy.urlEmpty });
      return;
    }

    try {
      await navigator.clipboard.writeText(publicBookingUrl);
      setCopiedUrl(true);
      setFeedback({ type: "success", message: copy.toasts.urlCopied });
    } catch (_error) {
      setFeedback({ type: "error", message: copy.toasts.urlCopyError });
    }
  };

  const uploadHeroBackground = async () => {
    if (!heroBgFile) {
      setFeedback({ type: "error", message: copy.toasts.pickImageFirst });
      return;
    }
    if (heroBgAsset?.asset_id || heroBgAsset?.id) {
      setFeedback({ type: "error", message: copy.toasts.heroAlreadyExists });
      return;
    }

    setHeroBgUploading(true);
    setFeedback({ type: "", message: "" });

    try {
      const formData = new FormData();
      formData.append("file", heroBgFile);
      if (businessUnitId) {
        formData.append("business_unit_id", String(businessUnitId));
      }
      formData.append("kind", "hero_background");

      await axios.post("/api/onboarding/booking-engine/assets/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setHeroBgFile(null);
      await refreshMediaSnapshot();
      setFeedback({ type: "success", message: copy.toasts.heroUploaded });
    } catch (error) {
      console.error("[Onboarding][BookingEngine] Error uploading hero image:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.toasts.heroError });
    } finally {
      setHeroBgUploading(false);
    }
  };

  const removeHeroBackground = async () => {
    if (!heroBgAsset?.asset_id && !heroBgAsset?.id) return;
    const confirmed = window.confirm(copy.actions.remove + "?");
    if (!confirmed) return;

    setHeroBgUploading(true);
    setFeedback({ type: "", message: "" });

    try {
      await axios.delete("/api/onboarding/booking-engine/assets/active", {
        params: {
          business_unit_id: businessUnitId || undefined,
          kind: "hero_background",
        },
      });

      setHeroBgAsset(null);
      setHeroBgFile(null);
      await refreshMediaSnapshot();
      setFeedback({ type: "success", message: copy.toasts.heroRemoved });
    } catch (error) {
      console.error("[Onboarding][BookingEngine] Error removing hero image:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.toasts.heroError });
    } finally {
      setHeroBgUploading(false);
    }
  };

  const setRoomImageBusy = (categoryId, nextValue) => {
    setRoomImageBusyByCategoryId((currentMap) => ({
      ...(currentMap || {}),
      [Number(categoryId)]: nextValue,
    }));
  };

  const uploadRoomCategoryImage = async (categoryId, kind) => {
    const resolvedCategoryId = Number(categoryId);
    const pickedFile = roomCategoryImageFileById?.[resolvedCategoryId] || null;
    const currentImages = roomCategoryImages?.[resolvedCategoryId] || [];
    if (!pickedFile) {
      setFeedback({ type: "error", message: copy.toasts.pickImageFirst });
      return;
    }
    if (currentImages.length >= 10) {
      setFeedback({ type: "error", message: copy.roomMaxImages });
      return;
    }

    setRoomImageBusy(resolvedCategoryId, true);
    setFeedback({ type: "", message: "" });

    try {
      const formData = new FormData();
      formData.append("file", pickedFile);
      if (businessUnitId) {
        formData.append("business_unit_id", String(businessUnitId));
      }
      formData.append("kind", kind);
      formData.append("entity_type", "onboarding_room_category");
      formData.append("entity_id", String(resolvedCategoryId));
      formData.append("onboarding_room_category_id", String(resolvedCategoryId));

      await axios.post("/api/onboarding/booking-engine/assets/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setRoomCategoryImageFileById((currentMap) => {
        const nextMap = { ...(currentMap || {}) };
        delete nextMap[resolvedCategoryId];
        return nextMap;
      });

      await refreshMediaSnapshot();
      setFeedback({ type: "success", message: copy.toasts.imageUploaded });
    } catch (error) {
      console.error("[Onboarding][BookingEngine] Error uploading room image:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.toasts.imageError });
    } finally {
      setRoomImageBusy(resolvedCategoryId, false);
    }
  };

  const removeRoomCategoryImage = async (categoryId, assetId) => {
    const resolvedCategoryId = Number(categoryId);
    const resolvedAssetId = Number(assetId);
    if (!resolvedCategoryId || !resolvedAssetId) return;

    setRoomImageBusy(resolvedCategoryId, true);
    setFeedback({ type: "", message: "" });

    try {
      await axios.post(`/api/onboarding/booking-engine/assets/${resolvedAssetId}/deactivate`, {
        onboarding_room_category_id: resolvedCategoryId,
      });
      await refreshMediaSnapshot();
      setFeedback({ type: "success", message: copy.toasts.imageRemoved });
    } catch (error) {
      console.error("[Onboarding][BookingEngine] Error removing room image:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.toasts.imageError });
    } finally {
      setRoomImageBusy(resolvedCategoryId, false);
    }
  };

  const setRoomCategoryImageAsCover = async (categoryId, assetId) => {
    const resolvedCategoryId = Number(categoryId);
    const resolvedAssetId = Number(assetId);
    if (!resolvedCategoryId || !resolvedAssetId) return;

    setRoomImageBusy(resolvedCategoryId, true);
    setFeedback({ type: "", message: "" });

    try {
      await axios.post(`/api/onboarding/booking-engine/assets/${resolvedAssetId}/make-cover`, {
        onboarding_room_category_id: resolvedCategoryId,
      });
      await refreshMediaSnapshot();
      setFeedback({ type: "success", message: copy.toasts.imageCoverUpdated });
    } catch (error) {
      console.error("[Onboarding][BookingEngine] Error updating cover image:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.toasts.imageError });
    } finally {
      setRoomImageBusy(resolvedCategoryId, false);
    }
  };

  const validateBeforeSave = useCallback(() => {
    for (const category of roomCategories) {
      const categoryId = Number(category?.id || 0);
      const row = normalizeBookingEngineRoomCopyRow(roomCategoryCopyById?.[categoryId]) || buildDefaultBookingEngineRoomCopy(category);
      if (!String(row.name_en || "").trim() || !String(row.name_es || "").trim()) {
        return copy.toasts.roomCopyMissingNames;
      }
    }
    return null;
  }, [copy.toasts.roomCopyMissingNames, roomCategories, roomCategoryCopyById]);

  const handleSave = useCallback(async () => {
    const validationError = validateBeforeSave();
    if (validationError) {
      setFeedback({ type: "error", message: validationError });
      return false;
    }

    setSaving(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await axios.put("/api/onboarding/booking-engine", {
        draft: buildBookingEngineDraftPayload(draft),
        profile: buildBookingEngineProfilePayload(profile),
        room_category_copy: buildBookingEngineRoomCopyPayload(roomCategoryCopyById, roomCategories),
        room_category_amenities: buildBookingEngineRoomAmenitiesPayload(roomAmenityIdsById),
      });

      const responseData = response.data || {};
      setDraft(normalizeBookingEngineDraft(responseData?.draft || responseData?.settings || responseData));
      setProfile(
        normalizeBookingEngineProfile(
          responseData?.profile || responseData?.booking_engine_profile || responseData?.public_profile
        )
      );
      setRoomCategoryCopyById(
        normalizeBookingEngineRoomCopyMap(
          responseData?.room_category_copy ||
            responseData?.room_category_copies ||
            responseData?.booking_engine_room_categories ||
            roomCategoryCopyById
        )
      );
      setRoomAmenityIdsById(
        normalizeBookingEngineRoomAmenities(
          responseData?.room_category_amenities ||
            responseData?.room_category_amenity_ids ||
            responseData?.amenities_by_category ||
            roomAmenityIdsById
        )
      );
      if (responseData?.session) {
        setSessionMeta(responseData.session);
      }
      setIsDirty(false);
      markStepComplete("booking-engine");
      setFeedback({ type: "success", message: copy.toasts.saveSuccess });
      return true;
    } catch (error) {
      console.error("[Onboarding][BookingEngine] Error saving draft:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.toasts.saveError });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    copy.toasts.saveError,
    copy.toasts.saveSuccess,
    draft,
    markStepComplete,
    profile,
    roomAmenityIdsById,
    roomCategories,
    roomCategoryCopyById,
    validateBeforeSave,
  ]);

  useEffect(() => {
    setStepCommandState("booking-engine", {
      onSave: handleSave,
      canSave: !loading && !loadFailed && !mediaBusy,
      canProceed: !isDirty && !loadFailed && !mediaBusy,
      isDirty,
      isSaving: saving || mediaBusy,
    });
    return () => clearStepCommandState("booking-engine");
  }, [clearStepCommandState, handleSave, isDirty, loadFailed, loading, mediaBusy, saving, setStepCommandState]);

  if (loading) {
    return (
      <div className="onboarding-emptyState">
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{copy.copy}</p>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="onboarding-emptyState onboarding-formCard--centered">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{feedback.message || copy.toasts.loadError}</p>
      </div>
    );
  }

  return (
    <div className="onboarding-grid">
      <style>{BOOKING_ENGINE_STEP_CSS}</style>

      <section className="onboarding-formCard">
        <div className="onboarding-formHeader">
          <div>
            <div className="onboarding-eyebrow">{copy.eyebrow}</div>
            <div className="onboarding-stepCount">
              {onboardingCopy.progress.stepCounter(stepNumber, steps.length)}
            </div>
            <h3 className="onboarding-sectionTitle">{copy.title}</h3>
            <p className="onboarding-sectionText">{copy.copy}</p>
          </div>
        </div>

        <div className="onboarding-bookingLayout">
          <div className="onboarding-bookingNotice">
            <strong>
              <FiStar size={16} />
              {copy.contractTitle}
            </strong>
            <div>{copy.contractCopy}</div>
          </div>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.launchTitle}</h4>
                <p>{copy.launchCopy}</p>
              </div>
              <span className="onboarding-settingsRoleBadge onboarding-settingsRoleBadge--highlight">
                {copy.contractTag}
              </span>
            </div>

            <div className="onboarding-bookingSplit">
              <div className="onboarding-fields">
                <label className="onboarding-field">
                  <span className="onboarding-label">{copy.slugLabel}</span>
                  <input
                    className="onboarding-input"
                    type="text"
                    value={draft.booking_engine_slug || ""}
                    placeholder={copy.slugPlaceholder}
                    onChange={(event) =>
                      updateDraft({
                        booking_engine_slug: String(event.target.value || "")
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, ""),
                      })
                    }
                  />
                  <span className="onboarding-sectionText">{copy.slugHint}</span>
                </label>

                <label className="onboarding-field">
                  <span className="onboarding-label">{copy.urlLabel}</span>
                  <input className="onboarding-input" type="text" value={publicBookingUrl || copy.urlEmpty} readOnly />
                  <span className="onboarding-sectionText">{copy.urlHint}</span>
                </label>

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button type="button" className="onboarding-secondaryBtn" onClick={handleCopyBookingUrl}>
                    <FiCopy size={15} />
                    {copiedUrl ? copy.toasts.urlCopied : copy.actions.copyUrl}
                  </button>
                </div>
              </div>

              <aside className="onboarding-bookingPreviewCard">
                <h5>{copy.profilePreviewTitle}</h5>
                <p>{copy.profilePreviewHint}</p>
                <div className="onboarding-bookingProfileLines">
                  {profilePreview.hotelName ? (
                    <div className="onboarding-bookingProfileName">{profilePreview.hotelName}</div>
                  ) : null}
                  {profilePreview.lines.length ? (
                    profilePreview.lines.map((line, index) => (
                      <div key={`profile-preview-line-${index}`}>{line}</div>
                    ))
                  ) : !profilePreview.hotelName ? (
                    <div>{copy.statusPending}</div>
                  ) : null}
                </div>
              </aside>
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.profileTitle}</h4>
                <p>{copy.profileCopy}</p>
              </div>
            </div>

            <div className="onboarding-fields onboarding-fields--threeCols">
              <label className="onboarding-field onboarding-field--full">
                <span className="onboarding-label">{copy.profileHotelName}</span>
                <input
                  className="onboarding-input"
                  type="text"
                  value={profile.public_hotel_name}
                  onChange={(event) => updateProfile({ public_hotel_name: event.target.value })}
                />
              </label>

              <label className="onboarding-field onboarding-field--full">
                <span className="onboarding-label">{copy.profileAddressLine}</span>
                <input
                  className="onboarding-input"
                  type="text"
                  value={profile.address_line}
                  onChange={(event) => updateProfile({ address_line: event.target.value })}
                />
              </label>

              <label className="onboarding-field">
                <span className="onboarding-label">{copy.profileCity}</span>
                <input
                  className="onboarding-input"
                  type="text"
                  value={profile.city}
                  onChange={(event) => updateProfile({ city: event.target.value })}
                />
              </label>

              <label className="onboarding-field">
                <span className="onboarding-label">{copy.profilePostalCode}</span>
                <input
                  className="onboarding-input"
                  type="text"
                  value={profile.postal_code}
                  onChange={(event) => updateProfile({ postal_code: event.target.value })}
                />
              </label>

              <label className="onboarding-field onboarding-field--full">
                <span className="onboarding-label">{copy.profileCountry}</span>
                <input
                  className="onboarding-input"
                  type="text"
                  value={profile.country}
                  onChange={(event) => updateProfile({ country: event.target.value })}
                />
              </label>

              <div className="onboarding-field onboarding-field--full">
                <span className="onboarding-label">{copy.profilePhones}</span>
                <div className="onboarding-bookingChipRow">
                  {(profile.phones || []).map((phone, index) => (
                    <span key={`phone-${phone}-${index}`} className="onboarding-bookingChip">
                      {phone}
                      <button
                        type="button"
                        onClick={() =>
                          updateProfile((currentProfile) => ({
                            ...currentProfile,
                            phones: (currentProfile.phones || []).filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="onboarding-bookingInlineAdd" style={{ marginTop: "10px" }}>
                  <input
                    className="onboarding-input"
                    type="text"
                    value={phoneInput}
                    onChange={(event) => setPhoneInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addProfilePhone();
                      }
                    }}
                  />
                  <button type="button" className="onboarding-secondaryBtn" onClick={addProfilePhone}>
                    <FiPlus size={14} />
                    {copy.actions.addPhone}
                  </button>
                </div>
              </div>

              <div className="onboarding-field onboarding-field--full">
                <span className="onboarding-label">{copy.profileEmails}</span>
                <div className="onboarding-bookingChipRow">
                  {(profile.emails || []).map((emailValue, index) => (
                    <span key={`email-${emailValue}-${index}`} className="onboarding-bookingChip">
                      {emailValue}
                      <button
                        type="button"
                        onClick={() =>
                          updateProfile((currentProfile) => ({
                            ...currentProfile,
                            emails: (currentProfile.emails || []).filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="onboarding-bookingInlineAdd" style={{ marginTop: "10px" }}>
                  <input
                    className="onboarding-input"
                    type="email"
                    value={emailInput}
                    onChange={(event) => setEmailInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addProfileEmail();
                      }
                    }}
                  />
                  <button type="button" className="onboarding-secondaryBtn" onClick={addProfileEmail}>
                    <FiPlus size={14} />
                    {copy.actions.addEmail}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.heroTitle}</h4>
                <p>{copy.heroCopy}</p>
              </div>
            </div>

            <div className="onboarding-bookingSplit">
              <div className="onboarding-fields">
                <label className="onboarding-field onboarding-field--full">
                  <span className="onboarding-label">{copy.heroBackgroundLabel}</span>
                  <input
                    className="onboarding-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={Boolean(heroBgAsset?.asset_id || heroBgAsset?.id)}
                    onChange={(event) => setHeroBgFile(event.target.files?.[0] || null)}
                  />
                  <span className="onboarding-sectionText">
                    {heroBgAsset?.public_url ? copy.heroBackgroundLocked : copy.heroBackgroundHint}
                  </span>
                </label>

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="onboarding-primaryBtn"
                    disabled={!heroBgFile || heroBgUploading || Boolean(heroBgAsset?.asset_id || heroBgAsset?.id)}
                    onClick={uploadHeroBackground}
                  >
                    <FiUpload size={15} />
                    {heroBgUploading ? copy.actions.uploading : copy.actions.upload}
                  </button>
                  <button
                    type="button"
                    className="onboarding-ghostBtn"
                    disabled={!heroBgAsset?.asset_id && !heroBgAsset?.id}
                    onClick={removeHeroBackground}
                  >
                    <FiTrash2 size={15} />
                    {copy.actions.remove}
                  </button>
                </div>

                <div className="onboarding-fields onboarding-fields--threeCols">
                  <label className="onboarding-field">
                    <span className="onboarding-label">{copy.heroSubtitleLabel} (EN)</span>
                    <input
                      className="onboarding-input"
                      type="text"
                      placeholder={copy.heroSubtitlePlaceholderEn}
                      value={getBookingEngineText(draft, "hero_subtitle", "en")}
                      onChange={(event) =>
                        updateDraft((currentDraft) => ({
                          ...currentDraft,
                          booking_engine_texts: {
                            ...(currentDraft.booking_engine_texts || {}),
                            hero_subtitle: {
                              ...((currentDraft.booking_engine_texts && currentDraft.booking_engine_texts.hero_subtitle) || {}),
                              en: event.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </label>

                  <label className="onboarding-field">
                    <span className="onboarding-label">{copy.heroSubtitleLabel} (ES)</span>
                    <input
                      className="onboarding-input"
                      type="text"
                      placeholder={copy.heroSubtitlePlaceholderEs}
                      value={getBookingEngineText(draft, "hero_subtitle", "es")}
                      onChange={(event) =>
                        updateDraft((currentDraft) => ({
                          ...currentDraft,
                          booking_engine_texts: {
                            ...(currentDraft.booking_engine_texts || {}),
                            hero_subtitle: {
                              ...((currentDraft.booking_engine_texts && currentDraft.booking_engine_texts.hero_subtitle) || {}),
                              es: event.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </label>

                  <label className="onboarding-field">
                    <span className="onboarding-label">{copy.heroClaimLabel} (EN)</span>
                    <input
                      className="onboarding-input"
                      type="text"
                      placeholder={copy.heroClaimPlaceholderEn}
                      value={getBookingEngineText(draft, "hero_claim", "en")}
                      onChange={(event) =>
                        updateDraft((currentDraft) => ({
                          ...currentDraft,
                          booking_engine_texts: {
                            ...(currentDraft.booking_engine_texts || {}),
                            hero_claim: {
                              ...((currentDraft.booking_engine_texts && currentDraft.booking_engine_texts.hero_claim) || {}),
                              en: event.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </label>

                  <label className="onboarding-field">
                    <span className="onboarding-label">{copy.heroClaimLabel} (ES)</span>
                    <input
                      className="onboarding-input"
                      type="text"
                      placeholder={copy.heroClaimPlaceholderEs}
                      value={getBookingEngineText(draft, "hero_claim", "es")}
                      onChange={(event) =>
                        updateDraft((currentDraft) => ({
                          ...currentDraft,
                          booking_engine_texts: {
                            ...(currentDraft.booking_engine_texts || {}),
                            hero_claim: {
                              ...((currentDraft.booking_engine_texts && currentDraft.booking_engine_texts.hero_claim) || {}),
                              es: event.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </label>
                </div>
              </div>

              <div
                className="onboarding-bookingHeroPreview"
                style={
                  heroBgAsset?.public_url
                    ? {
                        backgroundImage: `linear-gradient(180deg, rgba(6, 10, 18, 0.18), rgba(6, 10, 18, 0.72)), url(${heroBgAsset.public_url})`,
                      }
                    : undefined
                }
              >
                <div className="onboarding-bookingHeroCopy">
                  <span>{copy.heroPreviewTitle}</span>
                  <h4>{heroPreviewSubtitle}</h4>
                  <p>{heroPreviewClaim}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.pricingTitle}</h4>
                <p>{copy.pricingCopy}</p>
              </div>
            </div>

            <div className="onboarding-bookingSplit">
              <div className="onboarding-settingsPills">
                {[
                  { key: "booking_engine_price_includes_vat", label: copy.pricingVat },
                  {
                    key: "booking_engine_price_includes_other_taxes",
                    label: copy.pricingOtherTaxes,
                  },
                  { key: "booking_engine_price_includes_fees", label: copy.pricingFees },
                  { key: "booking_engine_price_includes_city_tax", label: copy.pricingCityTax },
                ].map((item) => {
                  const isActive = Boolean(draft[item.key]);
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`onboarding-settingsPill ${isActive ? "is-active" : ""}`}
                      onClick={() =>
                        updateDraft((currentDraft) => ({
                          ...currentDraft,
                          [item.key]: !currentDraft[item.key],
                        }))
                      }
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>

              <aside className="onboarding-bookingPricingPreview">
                <span className="onboarding-bookingPricingLabel">{copy.pricingSummaryTitle}</span>
                <span className="onboarding-bookingPricingValue">{copy.pricingTotalLabel}</span>
                <span>{pricingDisclosurePreview}</span>
              </aside>
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.emailsTitle}</h4>
                <p>{copy.emailsCopy}</p>
              </div>
            </div>

            <div className="onboarding-bookingLanguageGrid">
              {BOOKING_ENGINE_EDITOR_LANGUAGE_OPTIONS.map((option) => {
                const template = guestEmailTemplates?.[option.id] || { subject: "", body: "" };
                const renderedSubject = renderBookingEngineTemplateValue(
                  template.subject,
                  BOOKING_ENGINE_EMAIL_PREVIEW_VARS
                );
                const renderedBody = renderBookingEngineTemplateValue(
                  template.body,
                  BOOKING_ENGINE_EMAIL_PREVIEW_VARS
                );

                return (
                  <article key={`guest-email-${option.id}`} className="onboarding-bookingLanguageCard">
                    <div className="onboarding-bookingLanguageCardHead">
                      <h5>
                        {copy.emailsTitle} ({option.shortLabel})
                      </h5>
                      <span className="onboarding-settingsRoleBadge">
                        {String(template.subject || "").trim() || String(template.body || "").trim()
                          ? copy.statusConfigured
                          : copy.statusPending}
                      </span>
                    </div>

                    <div className="onboarding-bookingTokenWrap">
                      <div className="onboarding-bookingPricingLabel">{copy.emailVariablesTitle}</div>
                      <div className="onboarding-sectionText">{copy.emailVariablesHint}</div>
                      <div className="onboarding-bookingTokenRow">
                        {emailTokens.map((token) => (
                          <button
                            key={`${option.id}-${token.token}`}
                            type="button"
                            className="onboarding-bookingTokenBtn"
                            onClick={() => insertGuestEmailToken(option.id, token.token)}
                          >
                            {token.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="onboarding-field">
                      <span className="onboarding-label">{copy.emailSubjectLabel}</span>
                      <input
                        ref={(element) => setEmailFieldRef(option.id, "subject", element)}
                        className="onboarding-input"
                        type="text"
                        value={template.subject}
                        onFocus={() =>
                          setEmailInsertTargetByLang((currentMap) => ({ ...(currentMap || {}), [option.id]: "subject" }))
                        }
                        onChange={(event) => updateGuestEmailTemplate(option.id, { subject: event.target.value })}
                      />
                    </div>

                    <div className="onboarding-field">
                      <span className="onboarding-label">{copy.emailBodyLabel}</span>
                      <textarea
                        ref={(element) => setEmailFieldRef(option.id, "body", element)}
                        className="onboarding-textarea"
                        rows={10}
                        value={template.body}
                        onFocus={() =>
                          setEmailInsertTargetByLang((currentMap) => ({ ...(currentMap || {}), [option.id]: "body" }))
                        }
                        onChange={(event) => updateGuestEmailTemplate(option.id, { body: event.target.value })}
                      />
                      <span className="onboarding-sectionText">{copy.emailBodyHint}</span>
                    </div>

                    <div className="onboarding-bookingPreviewShell">
                      <h6>{copy.emailPreviewTitle}</h6>
                      <div>
                        <div className="onboarding-bookingPricingLabel">{copy.emailSubjectLabel}</div>
                        <div className="onboarding-bookingPreviewParagraph">
                          {renderedSubject || copy.emailEmptyPreview}
                        </div>
                      </div>
                      <div>
                        <div className="onboarding-bookingPricingLabel">{copy.emailBodyLabel}</div>
                        {buildBookingEnginePreviewParagraphs(renderedBody).length ? (
                          buildBookingEnginePreviewParagraphs(renderedBody).map((paragraph, index) => (
                            <p key={`guest-email-preview-${option.id}-${index}`} className="onboarding-bookingPreviewParagraph">
                              {paragraph}
                            </p>
                          ))
                        ) : (
                          <div className="onboarding-bookingPreviewParagraph">{copy.emailEmptyPreview}</div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.termsTitle}</h4>
                <p>{copy.termsCopy}</p>
              </div>
              <button type="button" className="onboarding-secondaryBtn" onClick={addTerm}>
                <FiPlus size={14} />
                {copy.actions.addTerm}
              </button>
            </div>

            <div className="onboarding-bookingTermsGrid">
              {draft.booking_engine_terms.length ? (
                draft.booking_engine_terms.map((term) => (
                  <article key={term.id} className="onboarding-bookingTermCard">
                    <div className="onboarding-bookingTermHead">
                      <div className="onboarding-bookingSummaryBadge">
                        {copy.termSortOrder}: {Number(term.sort_order) || 0}
                      </div>
                      <button type="button" className="onboarding-ghostBtn" onClick={() => removeTerm(term.id)}>
                        <FiTrash2 size={14} />
                        {copy.actions.remove}
                      </button>
                    </div>

                    <div className="onboarding-fields onboarding-fields--threeCols">
                      <label className="onboarding-field">
                        <span className="onboarding-label">{copy.termSortOrder}</span>
                        <input
                          className="onboarding-input"
                          type="number"
                          value={Number(term.sort_order) || 0}
                          onChange={(event) =>
                            updateTerm(term.id, (currentTerm) => ({
                              ...currentTerm,
                              sort_order: Number(event.target.value) || 0,
                            }))
                          }
                        />
                      </label>

                      <label className="onboarding-field">
                        <span className="onboarding-label">{copy.termTitleEn}</span>
                        <input
                          className="onboarding-input"
                          type="text"
                          value={term.title?.en || ""}
                          onChange={(event) =>
                            updateTerm(term.id, (currentTerm) => ({
                              ...currentTerm,
                              title: { ...(currentTerm.title || {}), en: event.target.value },
                            }))
                          }
                        />
                      </label>

                      <label className="onboarding-field">
                        <span className="onboarding-label">{copy.termTitleEs}</span>
                        <input
                          className="onboarding-input"
                          type="text"
                          value={term.title?.es || ""}
                          onChange={(event) =>
                            updateTerm(term.id, (currentTerm) => ({
                              ...currentTerm,
                              title: { ...(currentTerm.title || {}), es: event.target.value },
                            }))
                          }
                        />
                      </label>

                      <label className="onboarding-field onboarding-field--full">
                        <span className="onboarding-label">{copy.termTextEn}</span>
                        <textarea
                          className="onboarding-textarea"
                          rows={5}
                          value={term.text?.en || ""}
                          onChange={(event) =>
                            updateTerm(term.id, (currentTerm) => ({
                              ...currentTerm,
                              text: { ...(currentTerm.text || {}), en: event.target.value },
                            }))
                          }
                        />
                      </label>

                      <label className="onboarding-field onboarding-field--full">
                        <span className="onboarding-label">{copy.termTextEs}</span>
                        <textarea
                          className="onboarding-textarea"
                          rows={5}
                          value={term.text?.es || ""}
                          onChange={(event) =>
                            updateTerm(term.id, (currentTerm) => ({
                              ...currentTerm,
                              text: { ...(currentTerm.text || {}), es: event.target.value },
                            }))
                          }
                        />
                      </label>
                    </div>
                  </article>
                ))
              ) : (
                <div className="onboarding-emptyState onboarding-emptyState--compact">
                  <h4 className="onboarding-sectionTitle">{copy.termsTitle}</h4>
                  <p className="onboarding-sectionText">{copy.termEmpty}</p>
                </div>
              )}
            </div>
          </section>

          <section className="onboarding-settingsSection">
            <div className="onboarding-settingsSectionHead">
              <div>
                <h4>{copy.roomsTitle}</h4>
                <p>{copy.roomsCopy}</p>
              </div>
            </div>

            {!roomCategories.length ? (
              <div className="onboarding-emptyState onboarding-emptyState--compact">
                <h4 className="onboarding-sectionTitle">{copy.roomsTitle}</h4>
                <p className="onboarding-sectionText">{copy.roomsEmpty}</p>
              </div>
            ) : (
              <div className="onboarding-bookingRoomGrid">
                {roomCategories.map((category) => {
                  const categoryId = Number(category.id);
                  const images = sortBookingEngineImages(roomCategoryImages?.[categoryId] || []);
                  const mainImage = images.find((item) => item.kind === "cover") || images[0] || null;
                  const selectedAmenities = (roomAmenityIdsById?.[categoryId] || [])
                    .map((id) => amenitiesAll.find((amenity) => Number(amenity.id) === Number(id)))
                    .filter(Boolean);
                  const roomCopyRow =
                    normalizeBookingEngineRoomCopyRow(roomCategoryCopyById?.[categoryId]) ||
                    buildDefaultBookingEngineRoomCopy(category);
                  const activeTab = roomTabByCategoryId?.[categoryId] || "images";
                  const amenityQuery = String(roomAmenityQueryByCategoryId?.[categoryId] || "").trim().toLowerCase();
                  const amenityCategoryFilter = roomAmenityCategoryByCategoryId?.[categoryId] || "";
                  const filteredAmenities = amenitiesAll.filter((amenity) => {
                    const matchesCategory = !amenityCategoryFilter || amenity.category === amenityCategoryFilter;
                    const label = amenityLabel(amenity, languageId).toLowerCase();
                    const matchesQuery = !amenityQuery || label.includes(amenityQuery);
                    return matchesCategory && matchesQuery;
                  });
                  const groupedAmenities = filteredAmenities.reduce((accumulator, amenity) => {
                    const key = amenity.category || copy.roomAmenitiesAllCategories;
                    if (!accumulator[key]) {
                      accumulator[key] = [];
                    }
                    accumulator[key].push(amenity);
                    return accumulator;
                  }, {});

                  return (
                    <article key={`booking-room-category-${categoryId}`} className="onboarding-bookingRoomCard">
                      <div
                        className="onboarding-bookingRoomHero"
                        style={
                          mainImage?.public_url
                            ? {
                                backgroundImage: `linear-gradient(180deg, rgba(8, 12, 22, 0.12), rgba(8, 12, 22, 0.78)), url(${mainImage.public_url})`,
                              }
                            : undefined
                        }
                      >
                        <div className="onboarding-bookingRoomHeroInner">
                          <div className="onboarding-bookingChipRow">
                            <span className="onboarding-bookingChip">
                              {images.length}/10 {copy.roomImagesLabel}
                            </span>
                            <span className="onboarding-bookingChip">
                              {selectedAmenities.length} {copy.roomAmenitiesSelected}
                            </span>
                            <span className="onboarding-bookingChip">
                              {(roomCopyLanguageIsConfigured(category, roomCopyRow, "en") ||
                                roomCopyLanguageIsConfigured(category, roomCopyRow, "es"))
                                ? copy.statusConfigured
                                : copy.statusPending}
                            </span>
                          </div>
                          <h4>{category.name || `Room category ${categoryId}`}</h4>
                          <p>{mainImage?.public_url ? `${images.length} ${copy.roomImagesLabel.toLowerCase()}` : copy.roomNoImage}</p>
                        </div>
                      </div>

                      <div className="onboarding-bookingRoomBody">
                        <div className="onboarding-bookingRoomTabs">
                          {[
                            { id: "images", label: copy.roomImagesTab },
                            { id: "amenities", label: copy.roomAmenitiesTab },
                            { id: "copy", label: copy.roomCopyTab },
                          ].map((tab) => (
                            <button
                              key={`${categoryId}-${tab.id}`}
                              type="button"
                              className={`onboarding-bookingTab ${activeTab === tab.id ? "is-active" : ""}`}
                              onClick={() =>
                                setRoomTabByCategoryId((currentMap) => ({
                                  ...(currentMap || {}),
                                  [categoryId]: tab.id,
                                }))
                              }
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>

                        {activeTab === "images" ? (
                          <div className="onboarding-fields">
                            <label className="onboarding-field onboarding-field--full">
                              <span className="onboarding-label">{copy.roomImagesLabel}</span>
                              <input
                                className="onboarding-input"
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                disabled={images.length >= 10}
                                onChange={(event) =>
                                  setRoomCategoryImageFileById((currentMap) => ({
                                    ...(currentMap || {}),
                                    [categoryId]: event.target.files?.[0] || null,
                                  }))
                                }
                              />
                              <span className="onboarding-sectionText">
                                {images.length >= 10
                                  ? copy.roomMaxImages
                                  : roomCategoryImageFileById?.[categoryId]?.name || copy.roomNoFile}
                              </span>
                            </label>

                            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                              <button
                                type="button"
                                className="onboarding-primaryBtn"
                                disabled={!roomCategoryImageFileById?.[categoryId] || images.length >= 10 || roomImageBusyByCategoryId?.[categoryId]}
                                onClick={() => uploadRoomCategoryImage(categoryId, "cover")}
                              >
                                <FiUpload size={15} />
                                {roomImageBusyByCategoryId?.[categoryId] ? copy.actions.uploading : copy.roomUploadCover}
                              </button>
                              <button
                                type="button"
                                className="onboarding-secondaryBtn"
                                disabled={!roomCategoryImageFileById?.[categoryId] || images.length >= 10 || roomImageBusyByCategoryId?.[categoryId]}
                                onClick={() => uploadRoomCategoryImage(categoryId, "gallery")}
                              >
                                <FiUpload size={15} />
                                {roomImageBusyByCategoryId?.[categoryId] ? copy.actions.uploading : copy.roomUploadGallery}
                              </button>
                            </div>

                            {images.length ? (
                              <div className="onboarding-bookingThumbGrid">
                                {images.map((image) => (
                                  <article key={`room-image-${categoryId}-${image.asset_id || image.id}`} className="onboarding-bookingThumbCard">
                                    <div
                                      className="onboarding-bookingThumbMedia"
                                      style={image.public_url ? { backgroundImage: `url(${image.public_url})` } : undefined}
                                    />
                                    <div className="onboarding-bookingChipRow">
                                      <span className="onboarding-bookingChip">
                                        {image.kind === "cover" ? copy.roomCoverTag : copy.roomGalleryTag}
                                      </span>
                                    </div>
                                    <div className="onboarding-bookingThumbActions">
                                      {image.kind !== "cover" ? (
                                        <button
                                          type="button"
                                          className="onboarding-secondaryBtn"
                                          disabled={roomImageBusyByCategoryId?.[categoryId]}
                                          onClick={() => setRoomCategoryImageAsCover(categoryId, image.asset_id || image.id)}
                                        >
                                          <FiStar size={14} />
                                          {copy.actions.setAsCover}
                                        </button>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="onboarding-ghostBtn"
                                        disabled={roomImageBusyByCategoryId?.[categoryId]}
                                        onClick={() => removeRoomCategoryImage(categoryId, image.asset_id || image.id)}
                                      >
                                        <FiTrash2 size={14} />
                                        {copy.actions.remove}
                                      </button>
                                    </div>
                                  </article>
                                ))}
                              </div>
                            ) : (
                              <div className="onboarding-emptyState onboarding-emptyState--compact">
                                <h4 className="onboarding-sectionTitle">{copy.roomImagesLabel}</h4>
                                <p className="onboarding-sectionText">{copy.roomNoImage}</p>
                              </div>
                            )}
                          </div>
                        ) : activeTab === "amenities" ? (
                          <div className="onboarding-bookingAmenityList">
                            <div className="onboarding-bookingAmenityToolbar">
                              <input
                                className="onboarding-input"
                                type="text"
                                placeholder={copy.roomAmenitiesSearch}
                                value={roomAmenityQueryByCategoryId?.[categoryId] || ""}
                                onChange={(event) =>
                                  setRoomAmenityQueryByCategoryId((currentMap) => ({
                                    ...(currentMap || {}),
                                    [categoryId]: event.target.value,
                                  }))
                                }
                              />
                              <select
                                className="onboarding-input"
                                value={roomAmenityCategoryByCategoryId?.[categoryId] || ""}
                                onChange={(event) =>
                                  setRoomAmenityCategoryByCategoryId((currentMap) => ({
                                    ...(currentMap || {}),
                                    [categoryId]: event.target.value,
                                  }))
                                }
                              >
                                <option value="">{copy.roomAmenitiesAllCategories}</option>
                                {amenityCategories.map((categoryName) => (
                                  <option key={`amenity-category-${categoryId}-${categoryName}`} value={categoryName}>
                                    {categoryName}
                                  </option>
                                ))}
                              </select>
                              <button type="button" className="onboarding-ghostBtn" onClick={() => clearAmenities(categoryId)}>
                                <FiTrash2 size={14} />
                                {copy.roomClearAmenities}
                              </button>
                            </div>

                            <div>
                              <div className="onboarding-bookingPricingLabel">{copy.roomAmenitiesSelected}</div>
                              <div className="onboarding-bookingChipRow" style={{ marginTop: "10px" }}>
                                {selectedAmenities.length ? (
                                  selectedAmenities.map((amenity) => (
                                    <span key={`selected-amenity-${categoryId}-${amenity.id}`} className="onboarding-bookingChip">
                                      {amenityLabel(amenity, languageId)}
                                    </span>
                                  ))
                                ) : (
                                  <span className="onboarding-sectionText">{copy.roomNoAmenities}</span>
                                )}
                              </div>
                            </div>

                            {filteredAmenities.length ? (
                              Object.entries(groupedAmenities).map(([groupName, items]) => (
                                <div key={`amenity-group-${categoryId}-${groupName}`} className="onboarding-bookingAmenityCategory">
                                  <h6>{groupName}</h6>
                                  <div className="onboarding-bookingPillRow">
                                    {items.map((amenity) => {
                                      const isSelected = (roomAmenityIdsById?.[categoryId] || []).includes(Number(amenity.id));
                                      return (
                                        <button
                                          key={`amenity-pill-${categoryId}-${amenity.id}`}
                                          type="button"
                                          className={`onboarding-bookingPill ${isSelected ? "is-active" : ""}`}
                                          onClick={() => toggleAmenity(categoryId, amenity.id)}
                                        >
                                          {amenityLabel(amenity, languageId)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="onboarding-emptyState onboarding-emptyState--compact">
                                <h4 className="onboarding-sectionTitle">{copy.roomAmenitiesTitle}</h4>
                                <p className="onboarding-sectionText">{copy.roomNoAmenitiesMatch}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="onboarding-fields">
                            <BookingEngineRoomCopyPreview
                              category={category}
                              row={roomCopyRow}
                              image={mainImage}
                              previewLanguage={previewLanguage}
                              copy={copy}
                            />

                            <div className="onboarding-fields onboarding-fields--threeCols">
                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopyNameEn}</span>
                                <input
                                  className="onboarding-input"
                                  type="text"
                                  value={roomCopyRow.name_en}
                                  onChange={(event) => updateRoomCopy(category, { name_en: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopyNameEs}</span>
                                <input
                                  className="onboarding-input"
                                  type="text"
                                  value={roomCopyRow.name_es}
                                  onChange={(event) => updateRoomCopy(category, { name_es: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopySize}</span>
                                <input
                                  className="onboarding-input"
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={roomCopyRow.size_m2}
                                  onChange={(event) => updateRoomCopy(category, { size_m2: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopyBeds}</span>
                                <input
                                  className="onboarding-input"
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={roomCopyRow.beds_count}
                                  onChange={(event) => updateRoomCopy(category, { beds_count: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopyMaxPax}</span>
                                <input
                                  className="onboarding-input"
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={roomCopyRow.max_pax}
                                  onChange={(event) => updateRoomCopy(category, { max_pax: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field onboarding-field--full">
                                <span className="onboarding-label">{copy.roomCopyShortEn}</span>
                                <textarea
                                  className="onboarding-textarea"
                                  rows={4}
                                  value={roomCopyRow.short_description_en}
                                  onChange={(event) =>
                                    updateRoomCopy(category, { short_description_en: event.target.value })
                                  }
                                />
                              </label>

                              <label className="onboarding-field onboarding-field--full">
                                <span className="onboarding-label">{copy.roomCopyShortEs}</span>
                                <textarea
                                  className="onboarding-textarea"
                                  rows={4}
                                  value={roomCopyRow.short_description_es}
                                  onChange={(event) =>
                                    updateRoomCopy(category, { short_description_es: event.target.value })
                                  }
                                />
                              </label>

                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopyBedTypeEn}</span>
                                <input
                                  className="onboarding-input"
                                  type="text"
                                  value={roomCopyRow.bed_type_en}
                                  onChange={(event) => updateRoomCopy(category, { bed_type_en: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopyBedTypeEs}</span>
                                <input
                                  className="onboarding-input"
                                  type="text"
                                  value={roomCopyRow.bed_type_es}
                                  onChange={(event) => updateRoomCopy(category, { bed_type_es: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopyViewEn}</span>
                                <input
                                  className="onboarding-input"
                                  type="text"
                                  value={roomCopyRow.view_en}
                                  onChange={(event) => updateRoomCopy(category, { view_en: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field">
                                <span className="onboarding-label">{copy.roomCopyViewEs}</span>
                                <input
                                  className="onboarding-input"
                                  type="text"
                                  value={roomCopyRow.view_es}
                                  onChange={(event) => updateRoomCopy(category, { view_es: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field onboarding-field--full">
                                <span className="onboarding-label">{copy.roomCopyHighlightsEn}</span>
                                <textarea
                                  className="onboarding-textarea"
                                  rows={3}
                                  value={roomCopyRow.highlights_en}
                                  onChange={(event) => updateRoomCopy(category, { highlights_en: event.target.value })}
                                />
                              </label>

                              <label className="onboarding-field onboarding-field--full">
                                <span className="onboarding-label">{copy.roomCopyHighlightsEs}</span>
                                <textarea
                                  className="onboarding-textarea"
                                  rows={3}
                                  value={roomCopyRow.highlights_es}
                                  onChange={(event) => updateRoomCopy(category, { highlights_es: event.target.value })}
                                />
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {feedback.message ? (
          <div className={`onboarding-status ${feedback.type === "success" ? "onboarding-status--success" : "onboarding-status--error"}`}>
            {feedback.message}
          </div>
        ) : null}
      </section>

      <aside className="onboarding-infoCard">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.summaryTitle}</h3>
        <p className="onboarding-sectionText">{copy.summaryIntro}</p>
        <div className="onboarding-pointsList">
          {copy.summaryPoints.map((point) => (
            <div key={point} className="onboarding-pointItem">
              <span className="onboarding-pointDot" />
              <span>{point}</span>
            </div>
          ))}
        </div>

        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summarySlugLabel}</span>
          <span className="onboarding-kpiValue">{draft.booking_engine_slug || "-"}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryHeroLabel}</span>
          <span className="onboarding-kpiValue">
            {heroBgAsset?.public_url ? copy.statusReady : copy.statusPending}
          </span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryTermsLabel}</span>
          <span className="onboarding-kpiValue">{draft.booking_engine_terms.length}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryEmailsLabel}</span>
          <span className="onboarding-kpiValue">{configuredEmailLanguages}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryRoomsLabel}</span>
          <span className="onboarding-kpiValue">{`${configuredRoomCount}/${roomCategories.length}`}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryImagesLabel}</span>
          <span className="onboarding-kpiValue">{totalRoomImages}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryAmenitiesLabel}</span>
          <span className="onboarding-kpiValue">{totalSelectedAmenities}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summarySavedLabel}</span>
          <span className="onboarding-kpiValue">{formattedSavedAt}</span>
        </div>
      </aside>
    </div>
  );
}
